import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Database, Users, ArrowRight, UserPlus, Users as MeetingIcon, AlertTriangle, Building2 } from 'lucide-react';
import { Publication } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import MeetingModal from '../Meetings/MeetingModal';
import TeamAffiliationModal from './TeamAffiliationModal';

interface PublicationCardProps {
  publication: Publication;
}

const PublicationCard: React.FC<PublicationCardProps> = ({ publication }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isTeamAffiliationModalOpen, setIsTeamAffiliationModalOpen] = useState(false);
  const getStageColor = (stage: string) => {
    switch (stage) {
      case '1':
        return 'bg-blue-100 text-blue-800';
      case '2':
        return 'bg-yellow-100 text-yellow-800';
      case '3':
        return 'bg-purple-100 text-purple-800';
      case '4':
        return 'bg-indigo-100 text-indigo-800';
      case 'published':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1':
        return 'Ideation';
      case '2':
        return 'Review & Team';
      case '3':
        return 'Data Analysis';
      case '4':
        return 'Manuscript';
      case 'published':
        return 'Published';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const canJoinAsCoauthor = user && 
    user.id !== (publication.leadAuthorId || publication.lead_author_id) && 
    (publication.stage === '2' || publication.stage === '3') &&
    publication.status !== 'Rejected';

  const canCreateMeeting = user && 
    user.id === (publication.leadAuthorId || publication.lead_author_id) && 
    (publication.stage === '2' || publication.stage === '3' || publication.stage === '4') &&
    publication.status !== 'Rejected';

  const canManageTeamAffiliation = user && 
    (user.id === (publication.leadAuthorId || publication.lead_author_id) || 
     user.role === 'admin' || 
     user.role === 'superadmin') &&
    publication.status !== 'Rejected';

  const handleCardClick = () => {
    navigate(`/publications/${publication.id}`);
  };

  return (
    <>
      <div 
        onClick={handleCardClick}
        className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-green-300 hover:shadow-lg transition-all duration-300 group cursor-pointer"
      >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
            {publication.title}
          </h3>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStageColor(publication.stage)}`}>
              {getStageText(publication.stage)}
            </span>
            {(publication.priorityLevel === 'priority' || publication.priority_level === 'priority') && (
              <div className="flex items-center space-x-1">
                <AlertTriangle className="h-3 w-3 text-red-500" />
                <span className="text-xs text-red-600 font-medium">Priority</span>
                {(publication.followUpDate || publication.follow_up_date) && (
                  <span className="text-xs text-red-500">
                    (Due: {new Date(publication.followUpDate || publication.follow_up_date || '').toLocaleDateString()})
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
      </div>

      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
        <div className="flex items-center space-x-1">
          <Calendar className="h-4 w-4" />
          <span>
            {formatDate(
              // support both camelCase and snake_case from API
              (publication as any).submittedAt || (publication as any).submitted_at || ''
            ) || '—'}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <Database className="h-4 w-4" />
          <span>{publication.dataSource}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Users className="h-4 w-4" />
          <span>{publication.authorCount || publication.author_count || 1} author{(publication.authorCount || publication.author_count || 1) > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Author Avatars */}
      <div className="flex items-center space-x-2">
        <div className="flex -space-x-2">
          {publication.authors?.slice(0, 3).map((author, index) => (
            <div
              key={index}
              className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium border-2 border-white"
              title={`${author.firstName} ${author.lastName}`}
            >
              {getInitials(`${author.firstName} ${author.lastName}`)}
            </div>
          ))}
          {(publication.authorCount || publication.author_count || 1) > 3 && (
            <div className="w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium border-2 border-white">
              +{(publication.authorCount || publication.author_count || 1) - 3}
            </div>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {publication.leadAuthorName || publication.lead_author_name || 'Unknown Author'}
        </span>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
        <span className="flex items-center space-x-1 text-green-600 transition-colors">
          <span>View Details</span>
          <ArrowRight className="h-4 w-4" />
        </span>
        
        <div className="flex space-x-2">
          {/* Priority 1: Join as Co-author */}
          {canJoinAsCoauthor && (
            <Link
              to={`/publications/${publication.id}/coauthor`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <UserPlus className="h-3 w-3" />
              <span>Join as Co-author</span>
            </Link>
          )}

          {/* Priority 2: Teams */}
          {canManageTeamAffiliation && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsTeamAffiliationModalOpen(true);
              }}
              className="flex items-center space-x-1 bg-purple-600 text-white px-3 py-1 rounded-lg hover:bg-purple-700 transition-colors text-sm"
            >
              <Building2 className="h-3 w-3" />
              <span>Teams</span>
            </button>
          )}
          
          {/* Priority 3: Meeting (only if Teams not shown) */}
          {canCreateMeeting && !canManageTeamAffiliation && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMeetingModalOpen(true);
              }}
              className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <MeetingIcon className="h-3 w-3" />
              <span>Meeting</span>
            </button>
          )}
        </div>
      </div>
      </div>
    
    {/* Meeting Modal */}
    <MeetingModal
      isOpen={isMeetingModalOpen}
      onClose={() => setIsMeetingModalOpen(false)}
      publicationId={publication.id}
      publicationTitle={publication.title}
      leadAuthorId={publication.lead_author_id}
      onMeetingCreated={() => {
        // Refresh any relevant data if needed
        console.log('Meeting created successfully');
      }}
    />

    {/* Team Affiliation Modal */}
    <TeamAffiliationModal
      isOpen={isTeamAffiliationModalOpen}
      onClose={() => setIsTeamAffiliationModalOpen(false)}
      publicationId={publication.id}
      publicationTitle={publication.title}
      onSuccess={() => {
        // If we're already on any publication dashboard route, dispatch a refresh event
        // Otherwise, navigate to publications dashboard
        const isOnDashboard = location.pathname === '/publications' || 
                              location.pathname.startsWith('/dashboard/') ||
                              location.pathname.startsWith('/savic-lab-publications');
        if (isOnDashboard) {
          // Already on a dashboard - dispatch custom event to refresh data
          window.dispatchEvent(new CustomEvent('refreshPublications'));
        } else {
          // Navigate to publication system dashboard
          navigate('/publications', { replace: true });
        }
      }}
    />
    </>
  );
};

export default PublicationCard;
